import Link from "next/link";
import { useMemo, useState } from "react";

import DisplayBoard from "@/chessBoard/components/DisplayBoard";
import { getPostHref } from "@/lib/contentRouting";

const DEFAULT_POSITION =
  "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

function compact(values?: Array<string | null> | null) {
  return (
    values?.filter((value): value is string => Boolean(value?.trim())) || []
  );
}

function getFirstMove(moves?: string | null) {
  return (
    moves
      ?.trim()
      .match(/^1\.\s*([^\s]+)/)?.[1]
      ?.toLowerCase() || ""
  );
}

export default function OpeningDirectory({ openings }: { openings: any[] }) {
  const [query, setQuery] = useState("");

  const filteredOpenings = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return openings.filter((post) => {
      const opening = post?.opening || {};
      const searchable = [
        opening.name,
        opening.moves,
        post.title,
        ...compact(opening.aliases),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return !normalizedQuery || searchable.includes(normalizedQuery);
    });
  }, [openings, query]);

  const groups = useMemo(() => {
    const definitions = [
      { key: "e4", label: "1. e4" },
      { key: "d4", label: "1. d4" },
      { key: "other", label: "Other first moves" },
    ];

    return definitions
      .map((definition) => ({
        ...definition,
        openings: filteredOpenings.filter((post) => {
          const firstMove = getFirstMove(post?.opening?.moves);
          return definition.key === "other"
            ? firstMove !== "e4" && firstMove !== "d4"
            : firstMove === definition.key;
        }),
      }))
      .filter((group) => group.openings.length > 0);
  }, [filteredOpenings]);

  return (
    <div>
      <label className="block sm:max-w-[480px]">
        <span className="sr-only">Find an opening</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search openings"
          className="w-full rounded-lg border border-athens-gray-200 bg-white px-4 py-3 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-altblue-500 focus:ring-2 focus:ring-blue-100 sm:py-2.5 sm:text-sm"
        />
      </label>

      {filteredOpenings.length > 0 ? (
        <div className="mt-4 space-y-6">
          {groups.map((group) => (
            <section
              key={group.key}
              aria-labelledby={`group-${group.key}`}
              className="rounded-xl bg-white px-4 py-9 min-[360px]:px-6 sm:px-8"
            >
              <div className="mb-5 flex items-center gap-4">
                <h2
                  id={`group-${group.key}`}
                  className="shrink-0 text-lg font-bold tracking-tight text-slate-900"
                >
                  {group.label}
                </h2>
                <div className="h-px flex-1 bg-athens-gray-200" />
              </div>
              <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
                {group.openings.map((post) => {
                  const opening = post.opening || {};
                  const lessonSlug = opening.lessonSlug;
                  const referenceHref = getPostHref(post);
                  const name = opening.name || post.title;

                  return (
                    <article
                      key={post._sys.filename}
                      data-testid="opening-directory-card"
                      className="group mx-auto w-[240px] max-w-full"
                    >
                      <Link
                        href={referenceHref}
                        locale={false}
                        aria-label={`View ${name}`}
                        className="block"
                      >
                        <div className="mx-auto w-fit overflow-hidden rounded-md shadow-sm ring-1 ring-athens-gray-200 transition duration-200 ease-out group-hover:brightness-105 group-focus-within:brightness-105">
                          <DisplayBoard
                            fen={post.heroBoard?.fen || DEFAULT_POSITION}
                            playingWhite={post.heroBoard?.playingWhite ?? true}
                            boardSize={240}
                            arrows={post.heroBoard?.arrows || []}
                            highlights={post.heroBoard?.highlights || []}
                            ariaLabel={`${name} position`}
                          />
                        </div>
                        <h3 className="mt-4 text-lg font-bold tracking-tight text-slate-900">
                          {name}
                        </h3>
                        {opening.moves && (
                          <p className="mt-1 font-mono text-sm text-slate-500">
                            {opening.moves}
                          </p>
                        )}
                      </Link>

                      <div className="pt-2 text-sm font-semibold">
                        {lessonSlug && (
                          <Link
                            href={`/learn/openings/${lessonSlug}`}
                            locale={false}
                            className="text-slate-500 hover:text-slate-900"
                          >
                            Practice opening ↗
                          </Link>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="mt-8 rounded-lg border border-athens-gray-150 bg-white px-5 py-8 text-center text-sm text-slate-600">
          No openings match “{query}”.
        </div>
      )}
    </div>
  );
}
